/**
 * CookieConsent — bottom-left cookie consent banner.
 *
 * Shown once per visitor (persisted via `lib/cookieConsent.ts`) until
 * they explicitly Accept or Reject. "Read More" links to the existing
 * `/cookies` CMS page (see `cms/content/cookie-policy.html` /
 * `router/routes.tsx`'s `{ path: '/cookies', element: <PagePage slug="cookies" /> }`)
 * rather than duplicating the policy copy here.
 *
 * Re-opening: the footer's "Cookie Settings" link calls
 * `resetCookieConsent()`, which this component listens for via
 * `subscribeCookieConsent()`, so a visitor can change their mind later
 * without needing to clear browser storage manually.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  getCookieConsent,
  setCookieConsent,
  subscribeCookieConsent,
} from '@/lib/cookieConsent';

export function CookieConsent(): JSX.Element | null {
  const [visible, setVisible] = useState(false);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    // Small delay so the banner slides in after initial paint rather
    // than popping in instantly with the rest of the page.
    const initial = getCookieConsent();
    let timer: ReturnType<typeof setTimeout> | undefined;
    if (initial === null) {
      timer = setTimeout(() => setVisible(true), 600);
    }

    const unsubscribe = subscribeCookieConsent((value) => {
      if (value === null) {
        setClosing(false);
        setVisible(true);
      } else {
        setVisible(false);
      }
    });

    return () => {
      if (timer) clearTimeout(timer);
      unsubscribe();
    };
  }, []);

  const dismiss = (value: 'accepted' | 'rejected'): void => {
    setClosing(true);
    // Let the exit animation play before unmounting.
    setTimeout(() => {
      setCookieConsent(value);
      setClosing(false);
    }, 300);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      className={`fixed bottom-4 left-4 right-4 sm:left-6 sm:right-auto sm:bottom-6 z-[90] max-w-sm transition-all duration-300 ease-out ${
        closing ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'
      }`}
      style={{ willChange: 'transform, opacity' }}
    >
      <div className="rounded-2xl border border-slate-200 bg-white/95 p-5 shadow-2xl shadow-slate-900/10 backdrop-blur-md dark:border-slate-700 dark:bg-slate-900/95">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 text-white">
            <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5" aria-hidden="true">
              <path
                d="M12 2a10 10 0 100 20 10 10 0 000-20zm-1.5 5a1.5 1.5 0 110 3 1.5 1.5 0 010-3zm5 3a1.5 1.5 0 110 3 1.5 1.5 0 010-3zm-6 5a1.5 1.5 0 110 3 1.5 1.5 0 010-3zm4.5.5a1 1 0 102 0 1 1 0 00-2 0z"
                fill="currentColor"
              />
            </svg>
          </span>
          <div>
            <p className="text-sm font-semibold text-slate-900 dark:text-white">We use cookies</p>
            <p className="mt-1 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
              We use cookies to improve your experience, analyse traffic, and
              personalise content. Choose Accept to allow all cookies, or
              Reject to only use the essential ones.{' '}
              <Link
                to="/cookies"
                className="font-medium text-blue-600 underline underline-offset-2 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
              >
                Read more
              </Link>
            </p>
          </div>
        </div>

        <div className="mt-4 flex items-center gap-3">
          <button
            type="button"
            onClick={() => dismiss('accepted')}
            className="flex-1 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 px-4 py-2.5 text-sm font-semibold text-white shadow-md shadow-blue-500/25 transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/40 hover:scale-[1.02]"
          >
            Accept
          </button>
          <button
            type="button"
            onClick={() => dismiss('rejected')}
            className="flex-1 rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 transition-all duration-300 hover:bg-slate-100 hover:border-slate-400 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800 dark:hover:border-slate-500"
          >
            Reject
          </button>
        </div>
      </div>
    </div>
  );
}

export default CookieConsent;
